import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { GalleryItemType } from '~/components/Gallery/types';
import { useSingleDoubleClickHandler } from '~/components/v2/components/shared/gallery/useSingleDoubleClickHandler';

const DOUBLE_CLICK_DELAY_MS = 300;

export type AssetCardThumbnailProps = Omit<ComponentPropsWithoutRef<'button'>, 'onClick'> & {
  preSelectItem: () => void;
  confirmItemSelection: () => void;
  isSelectable: boolean;
  isHovering: boolean;
  isHighlighted: boolean;
  onClick?: () => void;
  itemType: GalleryItemType;
};

export const GallerySelectableCardTrigger = memo(
  forwardRef<HTMLButtonElement, AssetCardThumbnailProps>(
    (
      {
        className,
        confirmItemSelection,
        isSelectable,
        isHovering,
        isHighlighted,
        onClick,
        preSelectItem,
        itemType,
        ...restOfProps
      },
      forwardedRef,
    ) => {
      const { isAboveMediumScreen } = useBreakpointsContext();

      const shouldDelayClick =
        itemType === GalleryItemType.board ||
        itemType === GalleryItemType.topLevelBoard ||
        itemType === GalleryItemType.canvas;

      const { onMouseDown } = useSingleDoubleClickHandler({
        onSingleClickStart: preSelectItem,
        onSingleClickComplete: confirmItemSelection,
        shouldDelayClick,
        isEnabled: isAboveMediumScreen && isSelectable,
        delayMs: DOUBLE_CLICK_DELAY_MS,
      });

      return (
        // Important: Please make sure to read the comment below before change the element tag to something else
        <button
          className={cn(
            'absolute inset-0 size-full cursor-default outline-none group-data-[selected=false]/gallery-selectable-card:focus-visible:ring-2 group-data-[selected=false]/gallery-selectable-card:focus-visible:ring-blue-9',
            className,
          )}
          data-highlighted={isHighlighted}
          data-hovering={isHovering}
          data-is-selectable={isSelectable}
          data-testid="GALLERY_CARD_SELECTION_BUTTON"
          onMouseDown={onMouseDown}
          onClick={!isAboveMediumScreen || !isSelectable ? onClick : undefined}
          ref={forwardedRef}
          {...restOfProps}
        >
          <span
            /**
             * As long as the parent element is a `<button />` with absolute position, we need to apply
             * an invisible element with the same `absolute inset-0` position to the button to make it
             * draggable.
             *
             * Prior to this fix, we were able to use `minWidth: '-moz-available'` or `size-full` to
             * make it draggable. However, these hack no longer work at the time of writing this comment.
             *
             * Confirmed that this "hacky" fix was needed again in Oct 3, 2025.
             * @see https://air-labs-team.slack.com/archives/CSU3B8B3R/p1689187866653019
             */
            className="absolute inset-0"
          >
            <span className="sr-only">Click to select</span>
          </span>
        </button>
      );
    },
  ),
);

GallerySelectableCardTrigger.displayName = 'GallerySelectableCardTrigger';
